import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../components/Common/Pagination/Pagination';
import AddExpenseCategoryDrawer from './AddExpenseCategoryDrawer';
import api from '../../api/axios';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  Tag,
  Calendar,
  User,
  MoreHorizontal
} from 'lucide-react';
import styles from './ExpenseCategories.module.css';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';

const MySwal = withReactContent(Swal);

const ExpenseCategories = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pagination, setPagination] = useState({ total: 0 });
  
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    fetchCategories();
  }, [currentPage, searchTerm, perPage]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/expense-categories', {
        params: {
          page: currentPage,
          per_page: perPage,
          search: searchTerm
        }
      });
      if (response.data.success) {
        setCategories(response.data.data.data);
        setPagination({
          total: response.data.data.total
        });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load expense categories');
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (e, index) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left - 120
      });
      setOpenMenuIndex(index);
    }
  };

  const handleDelete = async (category) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Category</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Expense Category?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete <strong>${category.category_name}</strong>?<br/>
          Any expenses linked to this category might become uncategorized.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/expense-categories/${category.id}`);
          if (response.data.success) {
            toast.success('Category deleted successfully');
            fetchCategories();
          }
        } catch (error) {
          console.error('Error deleting category:', error);
          toast.error('Failed to delete category');
        }
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuIndex(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className={`${styles['categories-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Expense Categories</h1>
              <Breadcrumbs items={[
                { label: 'Finance', path: '/expenses' },
                { label: 'Expense Categories', path: '/finance/expense-categories' }
              ]} />
            </div>
            <button 
              className={styles['add-btn']}
              onClick={() => {
                setSelectedCategory(null);
                setIsDrawerOpen(true);
              }}
            >
              <Plus size={20} />
              <span>Add Category</span>
            </button>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search categories..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className={styles['data-view-container']}>
            <div className={styles['table-wrapper']}>
              <table className={styles['category-table']}>
                <thead>
                  <tr>
                    <th>CATEGORY NAME</th>
                    <th>CREATED BY</th>
                    <th>CREATED ON</th>
                    <th style={{ textAlign: 'center' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className={styles['loading-cell']}>
                        <div className={styles['loading-state']}>
                          <Loader2 size={40} className={styles['spinner']} />
                          <span>Loading categories...</span>
                        </div>
                      </td>
                    </tr>
                  ) : categories.length === 0 ? (
                    <NoData message="No expense categories found." colSpan={4} isTable={true} />
                  ) : categories.map((cat, index) => (
                    <tr key={cat.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Tag size={16} style={{ color: '#f97316' }} />
                          {cat.category_name}
                        </div>
                      </td>
                      <td>
                        <div className={styles['creator-info']}>
                          <span className={styles['creator-name']}>{cat.creator?.name || 'System'}</span>
                          <span className={styles['creator-role']}>{cat.creator ? 'Staff' : 'Admin'}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                          <Calendar size={14} />
                          {new Date(cat.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </td>
                      <td>
                        <div className={styles['action-wrapper']}>
                          <button 
                            className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                            onClick={(e) => toggleMenu(e, index)}
                          >
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles['category-cards']}>
              {loading ? (
                <div className={styles['loading-state-mobile']} style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} />
                  <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Syncing categories...</p>
                </div>
              ) : categories.length === 0 ? (
                <NoData message="No categories found." isTable={false} />
              ) : categories.map((cat, index) => (
                <div key={cat.id} className={styles['category-card']}>
                  <div className={styles['card-header']}>
                    <div className={styles['card-title']}>
                      <Tag size={18} style={{ color: '#f97316' }} />
                      <span className={styles['card-title-text']}>{cat.category_name}</span>
                    </div>
                    <button 
                      className={styles['mobile-action-btn']}
                      onClick={(e) => toggleMenu(e, index)}
                    >
                      <MoreVertical size={18} />
                    </button>
                  </div>

                  <div className={styles['card-details']}>
                    <div className={styles['card-detail-item']}>
                      <span className={styles['detail-label']}>Created By</span>
                      <span className={styles['detail-value']}>{cat.creator?.name || 'System'}</span>
                    </div>
                    <div className={styles['card-detail-item']}>
                      <span className={styles['detail-label']}>Created On</span>
                      <span className={styles['detail-value']}>
                        {new Date(cat.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {pagination.total > perPage && (
            <>
              <div className={styles['desktop-pagination']}>
                <Pagination
                  currentPage={currentPage}
                  totalItems={pagination.total}
                  itemsPerPage={perPage}
                  onPageChange={setCurrentPage}
                />
              </div>

              <div className={styles['mobile-pagination']}>
                <Pagination
                  currentPage={currentPage}
                  totalItems={pagination.total}
                  itemsPerPage={perPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}

          <AddExpenseCategoryDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onSuccess={fetchCategories}
            category={selectedCategory}
          />

          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className={styles['dropdown-item']} 
                onClick={() => {
                  setSelectedCategory(categories[openMenuIndex]);
                  setIsDrawerOpen(true);
                  setOpenMenuIndex(null);
                }}
              >
                <Pencil size={16} />
                <span>Edit Name</span>
              </button>
              <button 
                className={[styles['dropdown-item'], styles['deselect']].join(' ')} 
                onClick={() => {
                  handleDelete(categories[openMenuIndex]);
                  setOpenMenuIndex(null);
                }}
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
            </div>
          )}

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default ExpenseCategories;
